import { LoadingOutlined } from "@ant-design/icons";
import { useUserActions } from "_actions";
import { useProductActions } from "_actions/product.actions";
import { InvestmentDataType, SmallcaseInvestmentDataType } from "_interfaces";
import {
  cx,
  formatCurrency,
  formatDate,
  formatDecimal,
  formatPercentage,
} from "_lib/utils";
import {
  Breadcrumb,
  Button,
  MenuProps,
  message,
  Spin,
  Table,
  TableProps,
} from "antd";
import Dropdown from "antd/es/dropdown/dropdown";
import { EllipsisVerticalIcon, TrendingDown, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

export const SmallcasePage = ({
  gatewayInstance,
}: {
  gatewayInstance: any;
}) => {
  const params = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [investment, setInvestment] = useState<InvestmentDataType>();
  const [rebalance, setRebalance] = useState<boolean>(false);
  const [smallcase, setSmallcase] = useState<SmallcaseInvestmentDataType>();
  const [products, setProducts] = useState([]);
  const userActions = useUserActions();
  const productActions = useProductActions();

  useEffect(() => {
    userActions
      .getInvestments()
      .then(
        (data) => {
          setInvestment(data.data);
          const isc = data.data?.smallcase?.find(
            (item: any) => item.investment.iscid == params.iscid
          );
          setSmallcase(isc?.investment);
          isc?.actions.rebalance.length && setRebalance(true);
        },
        (error) => {
          console.error(error.response);
          return message.error("Failed to fetch investments");
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
    productActions.discoverSmallcases().then(
      (response) => {
        setProducts(response.data.data.smallcases);
      },
      (error) => {
        console.error(error);
        message.error("Failed to fetch other Smallcase products");
      }
    );
  }, []);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: "Manage",
      onClick: () => handleTransaction("MANAGE"),
    },
    {
      key: "2",
      label: "Exit",
      onClick: () => handleTransaction("EXIT"),
    },
  ];

  const handleTransaction = (action: string = "INVESTMORE") => {
    setIsLoading(true);
    if (!smallcase) {
      message.error("Smallcase not found");
      setIsLoading(false);
      return;
    }
    productActions
      .buySmallcase(action, smallcase.scid, smallcase.iscid)
      .then(
        (response) => {
          const transactionId = response.data.data.transactionId;

          gatewayInstance
            .triggerTransaction({
              transactionId: transactionId,
            })
            .then((txnResponse: any) => {
              console.debug("received response:", txnResponse);

              message.success("Successfully bought smallcase");
            })
            .catch((err: any) => {
              console.error("received error:", err.message);
              switch (err.message) {
                case "user_cancelled":
                  message.error(
                    "You have cancelled the transaction. Please try again",
                    3
                  );
                  break;
                case "market_closed":
                  message.error(
                    "Market has closed. Please try again during market hours",
                    3
                  );
                  break;
                case "order_pending":
                  message.error(
                    "Buy order is already in progress. Please try again after sometime",
                    3
                  );
                  break;
                case "already_subscribed":
                  message.error(
                    "You have already subscribed to this smallcase.",
                    3
                  );
                  break;
                default:
                  message.error(
                    "Failed to buy smallcase. Please try again after sometime",
                    3
                  );
                  break;
              }
            });
        },
        (error) => {
          console.error(error);
          message.error("Failed to buy smallcase");
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }
  if (!smallcase) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-slate-500">Smallcase not found</p>
      </div>
    );
  }

  return (
    <section className="mx-auto space-y-5">
      <Breadcrumb
        items={[
          {
            title: <Link to="/">Home</Link>,
          },
          {
            title: <Link to="/products">Smallcase</Link>,
          },
          {
            title: smallcase.name,
          },
        ]}
      />
      <div className="grid lg:grid-cols-5 items-center gap-8 bg-white rounded-lg p-4 hover:text-black">
        <div className="flex gap-4 col-span-2 items-center text-sm font-medium px-3">
          <img
            src={smallcase.imageUrl}
            alt={smallcase.name}
            width={80}
            className="w-20 h-20"
          />
          <div>
            <h3 className="text-lg font-semibold pb-2">{smallcase.name}</h3>
            <p className="text-xs">{smallcase.shortDescription}</p>
            {/* <p className="text-xs">{smallcase.shortDescription}</p> */}
          </div>
        </div>

        <div className="text-sm font-medium px-3">
          <p>Current Value</p>
          <div className="text-lg font-semibold">
            {formatCurrency(smallcase.returns.insights.currentValue)}
          </div>
        </div>
        <div className="text-sm font-medium px-3">
          <p>Total Returns</p>
          <div
            className={cx(
              "flex justify-start items-end text-lg font-semibold",
              smallcase.returns.insights.totalReturns > 0
                ? "text-green-500"
                : "text-red-500"
            )}
          >
            {formatCurrency(smallcase.returns.insights.totalReturns)}
            <span className="flex items-center gap-1 text-xs ms-2">
              {smallcase.returns.insights.totalReturns > 0 ? (
                <TrendingUp className="w-4" />
              ) : (
                <TrendingDown className="w-4" />
              )}
              {smallcase.returns.insights.totalReturnsPercent}%
            </span>
          </div>
        </div>

        <div className="text-center flex space-x-2">
          <Button
            type="primary"
            className="font-bold text-xs px-8"
            onClick={() => handleTransaction("INVESTMORE")}
          >
            Invest More
          </Button>
          <Dropdown menu={{ items }}>
            <Button
              type="default"
              title="More actions"
              icon={<EllipsisVerticalIcon className="h-4" />}
            />
          </Dropdown>
        </div>
      </div>
      <div className="grid lg:grid-cols-4 gap-5">
        <div className="bg-white rounded-lg col-span-3 p-4 lg:w-full w-[calc(100vw-16px)]">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">Performance</h3>
              <p className="text-slate-600 text-sm font-medium">
                Invested since {formatDate(smallcase.date)}
              </p>
            </div>
            {rebalance && (
              <Button
                color="primary"
                variant="outlined"
                className="font-bold text-xs px-8"
                onClick={() => handleTransaction("REBALANCE")}
              >
                Rebalance Now
              </Button>
            )}
          </div>

          <div className="py-8 overflow-scroll">
            <h4 className="font-bold mb-4">Constituents</h4>
            <Constituents
              constituents={smallcase.currentConfig.constituents
                .sort((a, b) => a.stockName.localeCompare(b.stockName))
                .map((item) => ({ ...item, key: item.ticker }))}
              total={smallcase.returns.insights.currentInvestment}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg h-fit space-y-5 p-4">
          <h3 className="text-xs font-bold">More Products</h3>
          {products.map((item: any, index) => (
            <Link
              to={"/products"}
              className="flex gap-4 hover:text-black"
              key={index}
            >
              <img
                src={item.info.imageUrl}
                alt={item.info.name}
                className="w-16 h-16"
              />
              <div>
                <h3 className="font-bold pb-2">{item.info.name}</h3>
                <p className="flex justify-start items-start font-medium">
                  {formatCurrency(item.stats.indexValue)}
                  <span
                    className={cx(
                      "flex items-center gap-1 text-xs text-green-500 ms-2",
                      item.stats.returns.yearly > 0
                        ? "text-green-500"
                        : "text-red-500"
                    )}
                  >
                    {item.stats.returns.yearly > 0 ? (
                      <TrendingUp className="w-4" />
                    ) : (
                      <TrendingDown className="w-4" />
                    )}
                    {(item.stats.returns.yearly * 100).toFixed(2)}%
                  </span>
                </p>
              </div>
            </Link>
          ))}
          <div className="w-full text-end">
            <Link to="/products" className="text-xs underline">
              View all products
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Constituents = ({
  constituents,
  total,
}: {
  constituents: any[];
  total: number;
}) => {
  const columns: TableProps["columns"] = [
    {
      title: "Stock",
      dataIndex: "stockName",
      key: "stockName",
      render: (value, record) => (
        <span className="font-medium">
          {value} ({record.ticker})
        </span>
      ),
    },
    {
      title: "Average Price (₹)",
      dataIndex: "averagePrice",
      key: "averagePrice",
      sorter: (a, b) => a.averagePrice - b.averagePrice,
      render: (value) => formatCurrency(value),
    },
    {
      title: "Returns (%)",
      dataIndex: "returns",
      key: "returns",
      sorter: (a, b) => a.returns - b.returns,
      render: (value) => (
        <span
          className={cx(
            value > 0 ? "text-green-500" : "text-red-500",
            "font-semibold"
          )}
        >
          {formatPercentage(value)}
        </span>
      ),
    },
    {
      title: "Weights",
      sorter: (a, b) => a.averagePrice * a.shares - b.averagePrice * b.shares,
      render: (record) => (
        <span>
          {formatDecimal((record.averagePrice * record.shares * 100) / total)}%
        </span>
      ),
    },
    {
      title: "Shares",
      dataIndex: "shares",
      key: "shares",
      sorter: (a, b) => a.shares - b.shares,
    },
  ];
  return (
    <Table columns={columns} dataSource={constituents} pagination={false} />
  );
};
